import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { ArrowLeft, Clock, MessageCircle, Star, User, Copy } from 'lucide-react';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import { BlogHeader } from './Blog';
import { apiRequest } from '../lib/utils';
import { formatBlogDate, readingTime, specLabel, authorName } from '../constants/blog';

const DoctorCard = ({ doc }) => (
  <div className="bg-white border border-slate-100 rounded-2xl p-5 flex flex-col" data-testid={`related-doctor-${doc.user_id}`}>
    <div className="flex items-center gap-3">
      <div className="w-14 h-14 rounded-full bg-slate-100 overflow-hidden flex items-center justify-center flex-shrink-0">
        {doc.avatar?.startsWith('http') ? <img src={doc.avatar} alt={doc.name} className="w-full h-full object-cover" /> : <User className="w-6 h-6 text-slate-300" />}
      </div>
      <div className="min-w-0">
        {doc.is_author && <span className="text-[10px] font-bold uppercase tracking-wider text-[#3B1C73]">Author</span>}
        <p className="font-semibold text-slate-900 truncate">{doc.role === 'doctor' && !/^dr/i.test(doc.name || '') ? 'Dr. ' : ''}{doc.name}</p>
        <p className="text-sm text-[#0F62FE]">{specLabel(doc.specialization)}</p>
      </div>
    </div>
    <div className="flex items-center gap-3 text-xs text-slate-500 mt-3">
      {doc.experience_years ? <span>{doc.experience_years}+ yrs exp</span> : null}
      <span className="flex items-center gap-1"><Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />{doc.rating || 0}</span>
      {doc.consultation_fee ? <span>₹{doc.consultation_fee}</span> : null}
    </div>
    <div className="flex gap-2 mt-4">
      <Link to={`/provider/${doc.user_id}`} className="flex-1 text-center text-sm border border-slate-200 rounded-lg py-2 hover:bg-slate-50">View profile</Link>
      <Link to={`/book/${doc.user_id}`} className="flex-1 text-center text-sm bg-[#0F62FE] text-white rounded-lg py-2 hover:bg-[#0050E6]" data-testid={`related-doctor-book-${doc.user_id}`}>Book</Link>
    </div>
  </div>
);

const ShareBar = ({ blog }) => {
  const shareUrl = `${window.location.origin}/api/share/blog/${blog.slug}`;
  const wa = `https://wa.me/?text=${encodeURIComponent(`${blog.title} — read on Physign: ${shareUrl}`)}`;
  const copy = async () => { await navigator.clipboard.writeText(shareUrl); toast.success('Link copied'); };
  return (
    <div className="flex flex-wrap items-center gap-2">
      <a href={wa} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe5a] text-white text-sm font-semibold px-4 py-2 rounded-full" data-testid="blog-whatsapp-share">
        <MessageCircle className="w-4 h-4" />Share on WhatsApp
      </a>
      <button onClick={copy} className="inline-flex items-center gap-2 border border-slate-200 text-slate-700 text-sm px-4 py-2 rounded-full hover:bg-slate-50" data-testid="blog-copy-link">
        <Copy className="w-4 h-4" />Copy link
      </button>
    </div>
  );
};

export default function BlogPost() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setData(null); setError(false);
    apiRequest(`/blogs/slug/${slug}`).then(setData).catch(() => setError(true));
    window.scrollTo(0, 0);
  }, [slug]);

  if (error) return (
    <div className="min-h-screen bg-white"><BlogHeader />
      <div className="text-center py-24" data-testid="blog-not-found"><p className="text-slate-600">This article could not be found.</p><Link to="/blog" className="text-[#3B1C73] font-semibold mt-3 inline-block">Back to blog</Link></div>
    </div>
  );
  if (!data) return <div className="min-h-screen flex items-center justify-center"><div className="spinner" /></div>;

  const { blog, related_doctors: doctors, more_posts: more } = data;
  const author = authorName(blog.author_name, blog.author_role);
  const jsonLd = { '@context': 'https://schema.org', '@type': 'BlogPosting', headline: blog.title, image: blog.cover_image || undefined, datePublished: blog.published_at, dateModified: blog.updated_at, author: { '@type': 'Person', name: author }, publisher: { '@type': 'Organization', name: 'Physign' } };

  return (
    <div className="min-h-screen bg-white" data-testid="blog-post-page">
      <SEO path={`/blog/${blog.slug}`} title={blog.seo_title || blog.title} description={blog.seo_description || blog.excerpt || `${blog.title} — read on Physign.`} image={blog.cover_image} schema={jsonLd} />
      <BlogHeader />
      <article className="max-w-3xl mx-auto px-6 pt-10 pb-12">
        <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#3B1C73]" data-testid="blog-back-link"><ArrowLeft className="w-4 h-4" />All articles</Link>
        {blog.category && <Link to={`/blog?category=${encodeURIComponent(blog.category)}`} className="block text-xs font-semibold uppercase tracking-wider text-[#3B1C73] mt-6">{blog.category}</Link>}
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mt-3 leading-tight" data-testid="blog-post-title">{blog.title}</h1>
        {blog.excerpt && <p className="text-lg text-slate-600 mt-4">{blog.excerpt}</p>}
        <div className="flex flex-wrap items-center justify-between gap-4 mt-6 pb-6 border-b border-slate-100">
          <p className="text-sm text-slate-500"><span className="font-medium text-slate-800">{author}</span> · {formatBlogDate(blog.published_at)} · <Clock className="w-3.5 h-3.5 inline -mt-0.5" /> {readingTime(blog.content)} min read</p>
          <ShareBar blog={blog} />
        </div>
        {blog.cover_image && <img src={blog.cover_image} alt={blog.title} className="w-full max-h-[460px] object-cover rounded-2xl mt-8" data-testid="blog-post-cover" />}
        <div className="blog-content mt-8" data-testid="blog-post-content" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(blog.content || '') }} />
        {blog.tags?.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-10">{blog.tags.map((t) => <span key={t} className="text-xs bg-slate-100 text-slate-600 px-3 py-1 rounded-full">#{t}</span>)}</div>
        )}
        <div className="mt-10 pt-6 border-t border-slate-100"><ShareBar blog={blog} /></div>
        <p className="text-xs text-slate-400 mt-8 italic">This article is for general information only and is not a substitute for professional medical advice. Please consult a qualified doctor for diagnosis and treatment.</p>
      </article>

      {doctors.length > 0 && (
        <section className="bg-purple-50/60 py-12 px-6" data-testid="blog-related-doctors">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-bold text-slate-900 mb-6">Consult a doctor</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">{doctors.map((d) => <DoctorCard key={d.user_id} doc={d} />)}</div>
          </div>
        </section>
      )}

      {more.length > 0 && (
        <section className="py-12 px-6" data-testid="blog-more-posts">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-bold text-slate-900 mb-6">More articles</h2>
            <div className="grid sm:grid-cols-3 gap-5">
              {more.map((m) => (
                <Link key={m.slug} to={`/blog/${m.slug}`} className="group">
                  <div className="h-36 bg-purple-50 rounded-xl overflow-hidden">{m.cover_image && <img src={m.cover_image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}</div>
                  <p className="font-semibold text-slate-900 mt-3 group-hover:text-[#3B1C73]">{m.title}</p>
                  <p className="text-xs text-slate-400 mt-1">{formatBlogDate(m.published_at)}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
